import { groupBy } from "lodash";
import { ReactElement } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import sanitizeHtml from "sanitize-html";

import { CopilotAction, CopilotMessage } from "../../api/types.gen.ts";
import { nodeLinkRegex } from "../../utils/regex-definitions.ts";
import { CopyButton } from "../CopyButton.tsx";
import { markdownComponents } from "./markdown-components.tsx";
import { RunQueryActionGroup } from "./RunQueryActionGroup.tsx";

export interface AIAssistantMessageProps {
  message: CopilotMessage;
}

// Allow custom copilot protocols so our link component can render entity/tool/node selectors.
// react-markdown's defaultUrlTransform only allows https?, ircs?, mailto, xmpp and strips the rest.
const urlTransform = (url: string): string => {
  if (
    url.startsWith("tool://") ||
    url.startsWith("entity://") ||
    url.startsWith("node://") ||
    url.startsWith("#")
  ) {
    return url;
  }
  return defaultUrlTransform(url);
};

// Preprocess markdown:
// 1) normalize node:// links -> #node- for in-page highlighting
// 2) strip unsupported XML-ish tags from LLM output while keeping inner text
const preprocessMarkdown = (content: string): string => {
  const withNodeLinksNormalized = content.replace(
    nodeLinkRegex,
    "[$1](#node-$2)",
  );

  // Remove any XML-style tag whose body is only a number
  // (e.g. "<search_quality_score>5</search_quality_score>",
  // "<result_quality_score>4.5</result_quality_score>").
  const withoutNumericOnlyXmlTags = withNodeLinksNormalized.replace(
    /<([a-zA-Z_][\w:-]*)\b[^>]*>\s*[-+]?\d+(?:\.\d+)?\s*<\/\1>/g,
    "",
  );

  return sanitizeHtml(withoutNumericOnlyXmlTags, {
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      a: ["href", "target", "rel"],
    },
    allowedSchemes: [
      ...sanitizeHtml.defaults.allowedSchemes,
      "entity",
      "node",
      "tool",
    ],
    allowedTags: sanitizeHtml.defaults.allowedTags,
  });
};

export const AIAssistantMessage = ({
  message,
}: AIAssistantMessageProps): ReactElement => {
  const processedContent = preprocessMarkdown(message.content);

  const renderActions = (actions: CopilotAction[]): ReactElement => {
    // `CopilotAction` no longer includes an explicit `type` discriminator.
    // All current actions are "run query action" style, grouped by queryAction ID.
    const runQueryActionGroups = groupBy(
      actions,
      (action) => action.params.queryAction?.id ?? "_missing",
    );

    return (
      <div className="mt-4 space-y-4">
        {Object.keys(runQueryActionGroups).length > 0 && (
          <div className="space-y-2">
            {Object.entries(runQueryActionGroups).map(
              ([queryActionId, actions]) => (
                <RunQueryActionGroup
                  actions={actions}
                  groupName={
                    actions[0]?.description?.split(" relationship on ")[0] ||
                    "Query Action"
                  }
                  key={queryActionId}
                />
              ),
            )}
          </div>
        )}
      </div>
    );
  };

  const renderedActions = message.actions
    ? renderActions(message.actions)
    : null;

  return (
    <div className="space-y-3">
      <div className="relative">
        <ReactMarkdown
          components={markdownComponents}
          urlTransform={urlTransform}
        >
          {processedContent}
        </ReactMarkdown>
        {renderedActions}
        {message.isPartial && (
          <div className="mt-2 text-xs italic text-gray-500">
            Response stopped by user
          </div>
        )}
        <div className="mt-2 flex justify-start">
          <CopyButton
            content={message.content}
            errorMessage="Failed to copy response to clipboard"
            successMessage="Response copied to clipboard"
            title="Copy response"
          />
        </div>
      </div>
    </div>
  );
};
